import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Search, UserPlus, Eye, X, CheckCircle, AlertCircle, Sun, Home, Shield
} from 'lucide-react';

export const AdminUsersTab = ({
  users, filteredUsers, searchTerm, setSearchTerm,
  setShowCreateUser, getRoleBadge, fetchUserDetail,
  handleChangeRole, handleToggleActive,
  userDetail, setUserDetail, setSelectedUser
}) => (
  <>
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-4">
          <CardTitle data-testid="users-tab-title">Utilisateurs ({users.length})</CardTitle>
          <div className="flex items-center gap-3">
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Rechercher..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
                data-testid="admin-search-users"
              />
            </div>
            <Button
              onClick={() => setShowCreateUser(true)}
              className="bg-green-600 hover:bg-green-700 text-white rounded-full px-5"
              data-testid="admin-create-user-btn"
            >
              <UserPlus className="w-4 h-4 mr-2" /> Créer un utilisateur
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b">
                <th className="text-left py-3 px-4 font-medium text-muted-foreground">Utilisateur</th>
                <th className="text-left py-3 px-4 font-medium text-muted-foreground">Type</th>
                <th className="text-left py-3 px-4 font-medium text-muted-foreground">Rôle</th>
                <th className="text-left py-3 px-4 font-medium text-muted-foreground">Statut</th>
                <th className="text-left py-3 px-4 font-medium text-muted-foreground">Inscription</th>
                <th className="text-right py-3 px-4 font-medium text-muted-foreground">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((u, i) => (
                <tr key={i} className="border-b hover:bg-muted/50" data-testid={`user-row-${u.id}`}>
                  <td className="py-3 px-4">
                    <div>
                      <p className="font-medium">{u.name}</p>
                      <p className="text-sm text-muted-foreground">{u.email}</p>
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <Badge className={u.user_type === 'producer' ? 'bg-green-100 text-green-700' : u.user_type === 'pro' || u.role === 'pro' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}>
                      {u.user_type === 'producer' ? (
                        <><Sun className="w-3 h-3 mr-1" /> Producteur</>
                      ) : u.user_type === 'pro' || u.role === 'pro' ? (
                        <><Eye className="w-3 h-3 mr-1" /> Professionnel</>
                      ) : (
                        <><Home className="w-3 h-3 mr-1" /> Consommateur</>
                      )}
                    </Badge>
                  </td>
                  <td className="py-3 px-4">{getRoleBadge(u.role)}</td>
                  <td className="py-3 px-4">
                    {u.is_active !== false ? (
                      <Badge className="bg-green-100 text-green-700">
                        <CheckCircle className="w-3 h-3 mr-1" /> Actif
                      </Badge>
                    ) : (
                      <Badge className="bg-red-100 text-red-700">
                        <AlertCircle className="w-3 h-3 mr-1" /> Inactif
                      </Badge>
                    )}
                  </td>
                  <td className="py-3 px-4 text-sm text-muted-foreground">
                    {u.created_at ? new Date(u.created_at).toLocaleDateString('fr-FR') : '-'}
                  </td>
                  <td className="py-3 px-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button variant="ghost" size="sm" onClick={() => fetchUserDetail(u.id)} className="text-blue-600 hover:bg-blue-50" data-testid={`view-user-${u.id}`}>
                        <Eye className="w-4 h-4" />
                      </Button>
                      <select
                        className="text-xs border rounded-lg px-2 py-1 bg-white cursor-pointer"
                        value={u.role || 'client'}
                        onChange={(e) => handleChangeRole(u.id, e.target.value)}
                        data-testid={`role-select-${u.id}`}
                      >
                        <option value="client">Client</option>
                        <option value="pro">Pro</option>
                        <option value="admin">Admin</option>
                      </select>
                      <Button
                        variant="ghost" size="sm"
                        onClick={() => handleToggleActive(u.id, u.is_active !== false)}
                        className={u.is_active !== false ? 'text-red-600 hover:bg-red-50' : 'text-green-600 hover:bg-green-50'}
                        data-testid={`toggle-active-${u.id}`}
                      >
                        {u.is_active !== false ? <X className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>

    {userDetail && (
      <Card className="mt-4" data-testid="user-detail-panel">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">Détails : {userDetail.user?.name}</CardTitle>
            <Button variant="ghost" size="sm" onClick={() => { setUserDetail(null); setSelectedUser(null); }}>
              <X className="w-4 h-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="space-y-3">
              <h4 className="font-semibold text-sm uppercase tracking-widest text-muted-foreground">Profil</h4>
              <div className="space-y-2 text-sm">
                <p><span className="text-muted-foreground">Email:</span> {userDetail.user?.email}</p>
                <p><span className="text-muted-foreground">Type:</span> {userDetail.user?.user_type}</p>
                <p><span className="text-muted-foreground">Rôle:</span> {getRoleBadge(userDetail.user?.role)}</p>
                <p><span className="text-muted-foreground">Inscription:</span> {userDetail.user?.created_at ? new Date(userDetail.user.created_at).toLocaleDateString('fr-FR') : '-'}</p>
                <p><span className="text-muted-foreground">Adresse:</span> {userDetail.user?.address || 'Non renseigné'}</p>
              </div>
            </div>
            <div className="space-y-3">
              <h4 className="font-semibold text-sm uppercase tracking-widest text-muted-foreground">Communautés ({userDetail.communities?.length || 0})</h4>
              {userDetail.communities?.length > 0 ? (
                <div className="space-y-2">
                  {userDetail.communities.map((c, i) => (
                    <div key={i} className="p-2 bg-muted/50 rounded-lg text-sm">
                      <p className="font-medium">{c.name}</p>
                      <p className="text-xs text-muted-foreground">{c.location}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">Aucune communauté</p>
              )}
            </div>
            <div className="space-y-3">
              <h4 className="font-semibold text-sm uppercase tracking-widest text-muted-foreground">Transactions récentes</h4>
              {userDetail.recent_transactions?.length > 0 ? (
                <div className="space-y-2">
                  {userDetail.recent_transactions.slice(0, 5).map((tx, i) => (
                    <div key={i} className="flex justify-between p-2 bg-muted/50 rounded-lg text-sm">
                      <span>{tx.energy_kwh?.toFixed(2)} kWh</span>
                      <span className="font-medium">{tx.total_amount?.toFixed(2)}€</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">Aucune transaction</p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    )}
  </>
);
